import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, Coins, ChevronDown, ChevronRight } from 'lucide-react';

const fmtCost = (c) => (c >= 1 ? `$${c.toFixed(2)}` : `$${(c || 0).toFixed(4)}`);
const fmtInt = (n) => (n || 0).toLocaleString('en-US');
const fmtAt = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

// Admin card (System page): real token-based AI costs + per-user audit log (questions only).
export const AiCostDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState(null);
  const [audit, setAudit] = useState({ items: [], total: 0, page: 0 });
  const [auditOpen, setAuditOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api.getAiCosts()
      .then(r => setCosts(r.data))
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load AI costs'))
      .finally(() => setLoading(false));
  }, []);

  const loadAudit = useCallback(async (page) => {
    setLoadingMore(true);
    try {
      const r = await api.getAiAudit(page);
      setAudit(prev => ({
        total: r.data.total, page,
        items: page === 1 ? r.data.items : [...prev.items, ...r.data.items],
      }));
    } catch { toast.error('Failed to load audit log'); }
    setLoadingMore(false);
  }, []);

  const toggleAudit = () => {
    const next = !auditOpen;
    setAuditOpen(next);
    if (next && audit.page === 0) loadAudit(1);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="ai-cost-dashboard">
      <div className="flex items-center gap-2 mb-1">
        <Coins className="w-4 h-4 text-amber-400" />
        <p className="font-heading text-base text-foreground">Ask OLTA AI — Kosten & Audit-Log</p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Echte Kosten aus den exakten Token-Zahlen jeder Anfrage (Preise s. Modell-Liste oben).
        Das Audit-Log speichert pro Nutzer die gestellten Fragen (keine Antworten).
        {' '}<a href="/ai-costs" className="text-sky-300 hover:underline" data-testid="ai-cost-manager-link">Alle KI-Funktionen → KI-Kostenmanager</a>
      </p>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : !costs ? null : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-border rounded-sm bg-secondary/30 p-3" data-testid="ai-cost-today">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Kosten heute</p>
              <p className="font-mono text-lg font-bold text-amber-400 mt-0.5">{fmtCost(costs.today.cost)}</p>
            </div>
            <div className="border border-border rounded-sm bg-secondary/30 p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Anfragen heute</p>
              <p className="font-mono text-lg font-bold text-foreground mt-0.5">{costs.today.requests}</p>
            </div>
            <div className="border border-border rounded-sm bg-secondary/30 p-3" data-testid="ai-cost-30d">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Kosten 30 Tage</p>
              <p className="font-mono text-lg font-bold text-amber-400 mt-0.5">{fmtCost(costs.last30.cost)}</p>
            </div>
            <div className="border border-border rounded-sm bg-secondary/30 p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Anfragen 30 Tage</p>
              <p className="font-mono text-lg font-bold text-foreground mt-0.5">{costs.last30.requests}</p>
            </div>
          </div>

          {costs.by_model.length > 0 && (
            <div data-testid="ai-cost-by-model">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Nach Modell (30 Tage)</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1 pr-2 font-medium">Modell</th>
                    <th className="py-1 pr-2 font-medium text-right">Anfragen</th>
                    <th className="py-1 pr-2 font-medium text-right hidden sm:table-cell">Tokens in / out</th>
                    <th className="py-1 font-medium text-right">Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.by_model.map((m, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-1.5 pr-2 text-foreground">{m.label}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{m.requests}</td>
                      <td className="py-1.5 pr-2 text-right font-mono text-muted-foreground hidden sm:table-cell">
                        {fmtInt(m.prompt_tokens)} / {fmtInt(m.completion_tokens)}
                      </td>
                      <td className="py-1.5 text-right font-mono text-amber-400">{fmtCost(m.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {costs.by_user.length > 0 && (
            <div data-testid="ai-cost-by-user">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Nach Nutzer (30 Tage)</p>
              <div className="space-y-1">
                {costs.by_user.map((u, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border-b border-border/50 py-1.5">
                    <span className="text-foreground">{u.user_name}</span>
                    <span className="font-mono text-muted-foreground">{u.requests} Anfrage{u.requests !== 1 ? 'n' : ''}
                      <span className="text-amber-400 ml-3">{fmtCost(u.cost)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <button onClick={toggleAudit} data-testid="ai-audit-toggle"
              className="flex items-center gap-1.5 text-xs text-foreground hover:text-blue-400 transition-colors font-medium">
              {auditOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Audit-Log — wer hat was gefragt? {audit.total > 0 && <span className="text-muted-foreground font-mono">({audit.total})</span>}
            </button>
            {auditOpen && (
              <div className="mt-2 space-y-1.5" data-testid="ai-audit-log">
                {audit.items.length === 0 && !loadingMore && (
                  <p className="text-xs text-muted-foreground">Noch keine Anfragen geloggt.</p>
                )}
                {audit.items.map((r, i) => (
                  <div key={i} className="border border-border/60 rounded-sm px-3 py-2 bg-secondary/20" data-testid={`ai-audit-row-${i}`}>
                    <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-muted-foreground">
                      <span>{fmtAt(r.at)} · <span className="text-foreground">{r.user_name || 'Unknown'}</span> · {r.scope === 'deal' ? (r.deal_name || 'Deal') : 'Global'} · {r.model}</span>
                      <span className="text-amber-400 shrink-0">{fmtCost(r.cost_usd)}</span>
                    </div>
                    <p className="text-xs text-foreground/90 mt-1">{r.question}</p>
                  </div>
                ))}
                {audit.items.length < audit.total && (
                  <Button variant="ghost" size="sm" onClick={() => loadAudit(audit.page + 1)} disabled={loadingMore}
                    data-testid="ai-audit-load-more"
                    className="h-7 px-3 text-xs text-muted-foreground border border-border rounded-sm">
                    {loadingMore ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : null} Mehr laden
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
